import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API, CATEGORIES, catLabel, rupiah, imgSrc, imageRatioClass, imagePositionClass } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Search, ShoppingCart, Lock } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { toast } from "sonner";

export default function Shop() {
  usePageMeta("Toko Online", "Belanja Tali PP LUMBA LUMBA & KUDA EMAS, Katrol LUMBA LUMBA, dan Timbangan Manual & Digital di toko online NAWADAH by NDH Official.");
  const { user } = useAuth();
  const { add } = useCart();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("");
  const [brand, setBrand] = useState("");
  const [sort, setSort] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      navigate("/masuk?next=/toko");
      return;
    }
    API.get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const brands = useMemo(() => [...new Set(products.map((p) => p.brand).filter(Boolean))], [products]);

  const filtered = useMemo(() => {
    let list = products.filter((p) => {
      if (cat && p.category !== cat) return false;
      if (brand && p.brand !== brand) return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
    const minPrice = (p) => Math.min(...(p.variants || []).map((v) => v.price).concat([Infinity]));
    if (sort === "termurah") list = [...list].sort((a, b) => minPrice(a) - minPrice(b));
    if (sort === "termahal") list = [...list].sort((a, b) => minPrice(b) - minPrice(a));
    if (sort === "nama") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [products, search, cat, brand, sort]);

  const addFirstVariant = (p) => {
    const v = (p.variants || [])[0];
    if (!v) return toast.error("Produk belum memiliki varian");
    add(p, v, 1);
    toast.success(`${p.name} (${v.label}) ditambahkan ke keranjang`);
  };

  const selectCls = "bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="shop-page">
      <div className="bg-navy py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-2">Toko NAWADAH by NDH Official</p>
          <h1 className="font-display text-3xl sm:text-4xl text-white tracking-tight">Etalase Produk</h1>
          <p className="text-slate-400 text-sm mt-2">Harga khusus member — pilih varian ukuran sesuai kebutuhan Anda.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap gap-3 mb-8" data-testid="shop-filters">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="shop-search"
              placeholder="Cari produk..."
              className="w-full bg-white border border-slate-300 rounded-md pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
            />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="shop-filter-category" className={selectCls}>
            <option value="">Semua Kategori</option>
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="shop-filter-brand" className={selectCls}>
            <option value="">Semua Merek</option>
            {brands.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="shop-sort" className={selectCls}>
            <option value="">Urutkan</option>
            <option value="termurah">Harga Termurah</option>
            <option value="termahal">Harga Termahal</option>
            <option value="nama">Nama A-Z</option>
          </select>
        </div>

        {user === null || loading ? (
          <p className="text-slate-500" data-testid="shop-loading">Memuat produk...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="shop-empty">
            <p className="text-slate-500">Tidak ada produk yang cocok dengan pencarian Anda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="shop-grid">
            {filtered.map((p) => {
              const prices = (p.variants || []).map((v) => v.price);
              const minP = prices.length ? Math.min(...prices) : 0;
              const totalStock = (p.variants || []).reduce((s, v) => s + Number(v.stock || 0), 0);
              return (
                <article key={p.id} data-testid={`shop-product-${p.id}`} className="bg-white border border-slate-200 rounded-md overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-transform flex flex-col">
                  <Link to={`/toko/${p.id}`} className={`block overflow-hidden bg-slate-100 ${imageRatioClass(p.image_ratios?.[0], "1:1")}`}>
                    <img src={imgSrc((p.images || [])[0])} alt={p.name} className={`w-full h-full object-cover ${imagePositionClass(p.image_positions?.[0])}`} />
                  </Link>
                  <div className="p-5 flex flex-col flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C9A227]">{p.brand || "NAWADAH by NDH Official"}</p>
                    <Link to={`/toko/${p.id}`} className="font-bold text-sm mt-1 hover:text-[#0B1F3A] line-clamp-2">{p.name}</Link>
                    <p className="text-xs text-slate-500 mt-1">{catLabel(p.category)}</p>
                    <p className="font-bold text-[#0B1F3A] mt-3 text-sm" data-testid={`price-${p.id}`}>
                      {prices.length > 1 ? `Mulai ${rupiah(minP)}` : rupiah(minP)}
                    </p>
                    <p className={`text-xs mt-1 ${totalStock > 0 ? "text-emerald-600" : "text-red-500"}`}>
                      {totalStock > 0 ? `Stok: ${totalStock} ${p.unit || "pcs"}` : "Stok habis"}
                    </p>
                    <div className="mt-4 flex gap-2 mt-auto">
                      <Link to={`/toko/${p.id}`} data-testid={`detail-${p.id}`} className="flex-1 text-center px-3 py-2.5 border border-slate-300 text-xs font-bold rounded-md hover:border-[#0B1F3A] transition-colors">
                        Detail
                      </Link>
                      <button
                        onClick={() => addFirstVariant(p)}
                        disabled={totalStock <= 0}
                        data-testid={`add-cart-${p.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-xs font-bold rounded-md transition-colors disabled:opacity-50"
                      >
                        <ShoppingCart className="h-3.5 w-3.5" /> + Keranjang
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
